import { computed, ref } from 'vue'
import type { AppTourFeature, AppTourSpec } from '@/constants/appTourSpecs'

export interface FeatureTourStep {
  name: string
  description: string
  target?: string
}

const SEEN_KEY = 'caion_app_tour_seen'

const active = ref(false)
const stepIndex = ref(0)
const steps = ref<FeatureTourStep[]>([])
const pageTitle = ref('')
const pageId = ref('')
const hole = ref({ top: 0, left: 0, width: 0, height: 0, ready: false })

let resizeObserver: ResizeObserver | null = null
let observedEl: Element | null = null
let measureRetryTimer: ReturnType<typeof setTimeout> | null = null

function readSeen(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SEEN_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function hasSeenTour(id: string) {
  return !!readSeen()[id]
}

export function markTourSeen(id: string) {
  if (!id) return
  const next = { ...readSeen(), [id]: true }
  localStorage.setItem(SEEN_KEY, JSON.stringify(next))
}

function clearObserver() {
  if (resizeObserver && observedEl) resizeObserver.unobserve(observedEl)
  observedEl = null
}

function clearMeasureRetry() {
  if (measureRetryTimer) {
    clearTimeout(measureRetryTimer)
    measureRetryTimer = null
  }
}

function bindHole(el: HTMLElement) {
  const pos = getComputedStyle(el).position
  if (pos !== 'fixed' && pos !== 'sticky') {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }

  const update = () => {
    const rect = el.getBoundingClientRect()
    const pad = 8
    hole.value = {
      top: Math.max(8, rect.top - pad),
      left: Math.max(8, rect.left - pad),
      width: Math.min(window.innerWidth - 16, rect.width + pad * 2),
      height: Math.min(window.innerHeight - 16, rect.height + pad * 2),
      ready: true,
    }
  }

  window.setTimeout(update, 220)
  update()

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(el)
    observedEl = el
  }
}

function measureTarget(target?: string, attempt = 0) {
  clearObserver()
  clearMeasureRetry()
  hole.value = { top: 0, left: 0, width: 0, height: 0, ready: false }
  if (!target || typeof document === 'undefined') return

  const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
  if (!el) {
    if (attempt < 8) {
      measureRetryTimer = setTimeout(() => measureTarget(target, attempt + 1), 120)
    }
    return
  }
  bindHole(el)
}

function isTourTargetReady(target?: string): boolean {
  if (!target || typeof document === 'undefined') return false
  const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
  if (!el) return false
  const style = getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

function buildSteps(spec: AppTourSpec): FeatureTourStep[] {
  const intro: FeatureTourStep = {
    name: spec.title,
    description: `${spec.summary}\n\n${spec.purpose}`,
  }
  const featureSteps = spec.features
    .filter((item: AppTourFeature) => !item.target || isTourTargetReady(item.target))
    .map((item) => ({
      name: item.name,
      description: item.description,
      target: item.target,
    }))
  return [intro, ...featureSteps]
}

function syncHole() {
  measureTarget(steps.value[stepIndex.value]?.target)
}

export function useFeatureTour() {
  const current = computed(() => steps.value[stepIndex.value] || null)
  const total = computed(() => steps.value.length)
  const isLast = computed(() => stepIndex.value >= steps.value.length - 1)
  const isFirst = computed(() => stepIndex.value <= 0)

  function startTour(spec: AppTourSpec) {
    if (active.value) stopTour()
    const next = buildSteps(spec)
    if (!next.length) return
    steps.value = next
    pageTitle.value = spec.title
    pageId.value = spec.id
    stepIndex.value = 0
    active.value = true
    document.body.classList.add('is-feature-tour')
    syncHole()
  }

  function stopTour() {
    if (pageId.value) markTourSeen(pageId.value)
    active.value = false
    steps.value = []
    stepIndex.value = 0
    pageId.value = ''
    clearObserver()
    clearMeasureRetry()
    hole.value = { top: 0, left: 0, width: 0, height: 0, ready: false }
    document.body.classList.remove('is-feature-tour')
  }

  function nextStep() {
    if (isLast.value) {
      stopTour()
      return
    }
    stepIndex.value += 1
    syncHole()
  }

  function prevStep() {
    if (isFirst.value) return
    stepIndex.value -= 1
    syncHole()
  }

  function refreshHole() {
    if (active.value) syncHole()
  }

  return {
    active,
    stepIndex,
    steps,
    pageTitle,
    hole,
    current,
    total,
    isLast,
    isFirst,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    refreshHole,
  }
}
